/**
 * A box of the 209 whose content is an ordered list of short records.
 *
 * Boxes 8, 9a and 13 all print this: a searchable list, one row editable at a
 * time, with row and bulk delete. They share this rather than each carrying its
 * own copy of the selection and edit-lock behaviour.
 *
 * Every commit writes the whole list, which is the shape the status summary
 * record stores: the caller gets the full array back and upserts it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ICS204_TABLE } from '../../AssignmentList/assignmentCardStyles';

/** Marks the blank row an Add click appends, which is editable on arrival. */
const APPENDED_ROW_ID = 'APPENDED_ROW_ID';

/** The string-valued fields of T, which are the only ones a text cell can edit. */
type TextKey<T> = {
  [K in keyof T]-?: T[K] extends string ? K & string : never;
}[keyof T];

export type ListColumn<T> = {
  key: TextKey<T>;
  label: string;
  placeholder?: string;
};

export type EditableListTableProps<T> = {
  /** Plural noun for the list, e.g. 'notable items'. */
  itemsLabel: string;
  /** Singular noun, e.g. 'Notable Item'. Titles the Add button. */
  itemLabel: string;
  testId: string;
  columns: ListColumn<T>[];
  /** An empty record, which Add appends and the row editor starts from. */
  blank: T;
  /** Field the search box matches, and which names a row in the button labels. */
  primaryKey: TextKey<T>;
  values: T[];
  readOnly?: boolean;
  onChange: (values: T[]) => void;
};

type Row<T> = { id: string; value: T };

/**
 * Reads one text field off a record.
 *
 * `TextKey` already limits callers to the string-valued fields, but that is not
 * something the compiler can prove while T is still generic, so the read goes
 * through here rather than scattering assertions over the cells.
 */
const textAt = <T,>(value: T, key: string): string =>
  String((value as Record<string, unknown>)[key] ?? '');

export function EditableListTable<T extends object>({
  itemsLabel,
  itemLabel,
  testId,
  columns,
  blank,
  primaryKey,
  values,
  readOnly = false,
  onChange,
}: EditableListTableProps<T>) {
  const nextId = useRef(0);
  const makeRows = useCallback(
    (items: T[]): Row<T>[] =>
      items.map((value) => ({ id: `row-${nextId.current++}`, value })),
    []
  );

  const [rows, setRows] = useState<Row<T>[]>(() => makeRows(values));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<T>(blank);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  // Take server state only between edits, so a refetch cannot overwrite a row
  // someone is part way through typing.
  const serialized = JSON.stringify(values);
  useEffect(() => {
    if (editingId !== null) return;
    setRows((current) => {
      if (JSON.stringify(current.map((row) => row.value)) === serialized) {
        return current;
      }
      setSelectedIds([]);
      return makeRows(values);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, editingId]);

  const commitList = (nextRows: Row<T>[]) => {
    setRows(nextRows);
    onChange(nextRows.map((row) => row.value));
  };

  const visibleRows = useMemo(() => {
    const query = search.toLowerCase();
    const matches = rows.filter((row) =>
      textAt(row.value, primaryKey).toLowerCase().includes(query)
    );
    // A blank row the user just added matches no search, and hiding the row
    // they are typing into would strand the edit.
    const editing = rows.find((row) => row.id === editingId);
    if (editing && !matches.includes(editing)) return [...matches, editing];
    return matches;
  }, [rows, search, editingId, primaryKey]);

  const allSelected =
    visibleRows.length > 0 && selectedIds.length === visibleRows.length;

  const addRow = () => {
    setRows((current) => [...current, { id: APPENDED_ROW_ID, value: blank }]);
    setSelectedIds([]);
    setDraft(blank);
    setEditingId(APPENDED_ROW_ID);
  };

  const saveEdit = () => {
    const edited = rows.map((row) =>
      row.id === editingId ? { ...row, value: draft } : row
    );
    // The appended row only earns a lasting id once it is committed.
    commitList(
      edited.map((row) =>
        row.id === APPENDED_ROW_ID
          ? { ...row, id: `row-${nextId.current++}` }
          : row
      )
    );
    setEditingId(null);
  };

  const cancelEdit = () => {
    // Cancelling a row that was only just added drops it, rather than leaving a
    // blank line behind that nothing would ever write.
    if (editingId === APPENDED_ROW_ID) {
      setRows((current) => current.filter((row) => row.id !== APPENDED_ROW_ID));
    }
    setEditingId(null);
  };

  const deleteRows = (ids: string[]) => {
    commitList(rows.filter((row) => !ids.includes(row.id)));
    setSelectedIds([]);
  };

  const isLocked = editingId !== null;
  const rowName = (row: Row<T>) =>
    textAt(row.value, primaryKey) || `new ${itemLabel}`;

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Input
          className="tw:max-w-xs"
          aria-label={`Search ${itemsLabel}`}
          placeholder={`Search ${itemsLabel}...`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            className="tw:ml-auto"
            disabled={isLocked}
            onClick={addRow}
          >
            <Plus className="tw:size-4" />
            Add {itemLabel}
          </Button>
        )}
      </div>

      {!readOnly && selectedIds.length > 0 && (
        <div className="tw:flex tw:items-center tw:justify-between">
          <span className="tw:text-sm">{selectedIds.length} selected</span>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => deleteRows(selectedIds)}
          >
            <Trash2 className="tw:size-4" />
            Delete Selected
          </Button>
        </div>
      )}

      <Table className={ICS204_TABLE} data-testid={testId}>
        <TableHeader>
          <TableRow>
            <TableHead className="tw:w-10">
              <Checkbox
                aria-label={`Select all ${itemsLabel}`}
                checked={allSelected}
                disabled={visibleRows.length === 0 || isLocked}
                onCheckedChange={() =>
                  setSelectedIds(
                    allSelected ? [] : visibleRows.map((row) => row.id)
                  )
                }
              />
            </TableHead>
            {columns.map((column) => (
              <TableHead key={column.key}>{column.label}</TableHead>
            ))}
            <TableHead className="tw:w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + 2}
                className="tw:text-center"
              >
                {search
                  ? 'No results!'
                  : `There are no ${itemsLabel} yet. Click Add ${itemLabel} to add one.`}
              </TableCell>
            </TableRow>
          ) : (
            visibleRows.map((row) => {
              const editing = row.id === editingId;
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    <Checkbox
                      aria-label={`Select ${rowName(row)}`}
                      checked={selectedIds.includes(row.id)}
                      disabled={isLocked}
                      onCheckedChange={() =>
                        setSelectedIds((current) =>
                          current.includes(row.id)
                            ? current.filter((id) => id !== row.id)
                            : [...current, row.id]
                        )
                      }
                    />
                  </TableCell>
                  {columns.map((column, columnIndex) => (
                    <TableCell key={column.key}>
                      {editing ? (
                        <Input
                          autoFocus={columnIndex === 0}
                          aria-label={column.label}
                          placeholder={
                            column.placeholder ??
                            `Enter ${column.label.toLowerCase()}`
                          }
                          value={textAt(draft, column.key)}
                          onChange={(event) =>
                            setDraft((current) => ({
                              ...current,
                              [column.key]: event.target.value,
                            }))
                          }
                        />
                      ) : (
                        <span>{textAt(row.value, column.key)}</span>
                      )}
                    </TableCell>
                  ))}
                  <TableCell>
                    <div className="tw:flex tw:justify-end tw:gap-1">
                      {editing ? (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Save ${itemLabel}`}
                            onClick={saveEdit}
                          >
                            <Check className="tw:size-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Cancel ${itemLabel}`}
                            onClick={cancelEdit}
                          >
                            <X className="tw:size-4" />
                          </Button>
                        </>
                      ) : readOnly ? null : (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Edit ${rowName(row)}`}
                            disabled={isLocked}
                            onClick={() => {
                              setSelectedIds([]);
                              setDraft(row.value);
                              setEditingId(row.id);
                            }}
                          >
                            <Pencil className="tw:size-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Delete ${rowName(row)}`}
                            disabled={isLocked}
                            onClick={() => deleteRows([row.id])}
                          >
                            <Trash2 className="tw:size-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <span className="tw:text-sm tw:text-muted-foreground">
        {visibleRows.length} {itemsLabel}
      </span>
    </div>
  );
}

export default EditableListTable;
